import { parseJSON, isAfter, isBefore, addHours, differenceInMinutes, format } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';

export type GameStatus = 
  | 'upcoming'     // Future game
  | 'active'       // Game time has started (within game duration)
  | 'needs_scoring'// Game ended but no scores submitted
  | 'completed'    // Game scored and finalized
  | 'expired'      // Game time passed without completion
  | 'missed'       // Game time passed without being played
  | 'cancelled'    // Game was cancelled
  | 'scheduled';   // Default status

export interface GameStatusInfo {
  status: GameStatus;
  label: string;
  color: string;
  bgColor: string;
  description: string;
  canScore: boolean;
  canApply: boolean;
  priority: number; // For sorting
}

export const getGameDateTime = (date: string, time: string = '00:00', timezone: string = 'UTC'): Date => {
  try {
    // Ensure time has seconds
    let normalizedTime = time;
    if (!time.includes(':')) {
      normalizedTime = `${time}:00:00`;
    } else if (time.split(':').length === 2) {
      normalizedTime = `${time}:00`;
    }
    
    const dateTimeString = `${date}T${normalizedTime}`;
    // Parse the date/time in the specified timezone
    return fromZonedTime(dateTimeString, timezone);
  } catch {
    return new Date();
  }
};

export const calculateGameStatus = (
  gameDate: string,
  gameTime: string = '00:00',
  gameStatus?: string,
  duration: number = 2, // Default 2 hours game duration
  gameTimezone: string = 'UTC' // Timezone for the game
): GameStatus => {
  if (!gameDate) return 'scheduled';
  
  // For newly created games, don't auto-cancel based on database status
  // Only respect explicit completion or in_progress status
  if (gameStatus === 'completed') return 'completed';
  if (gameStatus === 'in_progress') return 'active';
  
  // Only return cancelled if it was explicitly set to cancelled
  // Don't auto-cancel based on other factors
  if (gameStatus === 'cancelled') return 'cancelled';
  
  const gameDateTime = getGameDateTime(gameDate, gameTime, gameTimezone);
  const gameEndTime = addHours(gameDateTime, duration);
  const now = new Date();
  
  // Game hasn't started yet (future games should always be upcoming)
  if (isAfter(gameDateTime, now)) {
    return 'upcoming';
  }
  
  // Game is currently happening
  if (isBefore(gameDateTime, now) && isAfter(gameEndTime, now)) {
    return 'active';
  }
  
  // Game ended recently (within 24 hours) - needs scoring
  const hoursAfterGame = differenceInMinutes(now, gameEndTime) / 60;
  if (hoursAfterGame <= 24) {
    return 'needs_scoring';
  }
  
  // Game ended more than 24 hours ago without completion
  return hoursAfterGame <= 72 ? 'missed' : 'expired';
};

export const getGameStatusInfo = (status: GameStatus): GameStatusInfo => {
  const statusMap: Record<GameStatus, GameStatusInfo> = {
    upcoming: {
      status: 'upcoming',
      label: 'Upcoming',
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10 border-blue-500',
      description: 'Game scheduled for the future',
      canScore: false,
      canApply: true,
      priority: 1
    },
    active: {
      status: 'active',
      label: 'Live Game',
      color: 'text-red-500',
      bgColor: 'bg-red-500/10 border-red-500',
      description: 'Game is currently in progress',
      canScore: true,
      canApply: false,
      priority: 0
    },
    needs_scoring: {
      status: 'needs_scoring',
      label: 'Needs Scoring',
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-500/10 border-yellow-500',
      description: 'Game ended, awaiting score submission',
      canScore: true,
      canApply: false,
      priority: 2
    },
    completed: {
      status: 'completed',
      label: 'Completed',
      color: 'text-green-500',
      bgColor: 'bg-green-500/10 border-green-500',
      description: 'Game completed with scores recorded',
      canScore: false,
      canApply: false,
      priority: 4
    },
    missed: {
      status: 'missed',
      label: 'Missed Game',
      color: 'text-red-600',
      bgColor: 'bg-red-600/10 border-red-600',
      description: 'Game time passed without being played',
      canScore: false,
      canApply: false,
      priority: 0
    },
    expired: {
      status: 'expired',
      label: 'Expired',
      color: 'text-gray-500',
      bgColor: 'bg-gray-500/10 border-gray-500',
      description: 'Game time passed without completion',
      canScore: false,
      canApply: false,
      priority: 5
    },
    cancelled: {
      status: 'cancelled',
      label: 'Cancelled',
      color: 'text-red-400',
      bgColor: 'bg-red-400/10 border-red-400',
      description: 'Game was cancelled',
      canScore: false,
      canApply: false,
      priority: 6
    },
    scheduled: {
      status: 'scheduled',
      label: 'Scheduled',
      color: 'text-gray-400',
      bgColor: 'bg-gray-400/10 border-gray-400',
      description: 'Game scheduled, awaiting details',
      canScore: false,
      canApply: true,
      priority: 3
    }
  };
  
  return statusMap[status];
};

export const getTimeUntilGame = (gameDate: string, gameTime: string = '00:00', gameTimezone: string = 'UTC'): string => {
  const gameDateTime = getGameDateTime(gameDate, gameTime, gameTimezone);
  const now = new Date();
  
  if (isBefore(gameDateTime, now)) {
    return 'Game has started';
  }
  
  const minutesUntil = differenceInMinutes(gameDateTime, now);
  
  if (minutesUntil < 60) {
    return `${minutesUntil}m until game`;
  }
  
  if (minutesUntil < 1440) { // Less than 24 hours
    const hours = Math.floor(minutesUntil / 60);
    return `${hours}h until game`;
  }
  
  const days = Math.floor(minutesUntil / 1440);
  return `${days}d until game`;
};

export const formatGameDateTime = (date: string, time: string = '00:00', gameTimezone: string = 'UTC'): string => {
  try {
    const gameDateTime = getGameDateTime(date, time, gameTimezone);
    return format(gameDateTime, 'MMM dd, yyyy \'at\' h:mm a');
  } catch {
    return `${date} at ${time}`;
  }
};

export const shouldShowInApplications = (gameDate: string, gameTime: string = '00:00', gameTimezone: string = 'UTC'): boolean => {
  const status = calculateGameStatus(gameDate, gameTime, undefined, 2, gameTimezone);
  return status === 'upcoming' || status === 'scheduled';
};

export const sortGamesByStatus = <T extends { game: { game_date: string; game_time: string; game_status?: string; game_timezone?: string } }>(
  games: T[]
): T[] => {
  return games.sort((a, b) => {
    const statusA = calculateGameStatus(a.game.game_date, a.game.game_time, a.game.game_status, 2, a.game.game_timezone || 'UTC');
    const statusB = calculateGameStatus(b.game.game_date, b.game.game_time, b.game.game_status, 2, b.game.game_timezone || 'UTC');
    
    const infoA = getGameStatusInfo(statusA);
    const infoB = getGameStatusInfo(statusB);
    
    // Sort by priority first, then by game date
    if (infoA.priority !== infoB.priority) {
      return infoA.priority - infoB.priority;
    }
    
    // If same priority, sort by date (newest first for active/needs scoring, oldest first for upcoming)
    const dateA = getGameDateTime(a.game.game_date, a.game.game_time, a.game.game_timezone || 'UTC');
    const dateB = getGameDateTime(b.game.game_date, b.game.game_time, b.game.game_timezone || 'UTC');
    
    if (statusA === 'upcoming' || statusA === 'scheduled') {
      return dateA.getTime() - dateB.getTime(); // Ascending for upcoming
    }
    
    return dateB.getTime() - dateA.getTime(); // Descending for others
  });
};